import classNames from 'classnames';
import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { IParentProps } from '../../shared/types/IParentProps';
import { IQATestableProps } from '../../shared/types/IQATestableProps';
import { ISetState } from '../../shared/types/ISetState';
import { IStyleableProps } from '../../shared/types/IStyleableProps';
import { createLayoutSlot } from '../layout-slot/createLayoutSlot';
import { useSlot } from '../layout-slot/useSlot';

import { CollapseIcon } from './collapse-icon/CollapseIcon';
import styles from './Collapse.module.css';
import { COLLAPSE_SIZES } from './COLLAPSE_SIZES';
import { COLLAPSE_STYLES } from './COLLAPSE_STYLES';

export type ICollapseProps = IParentProps &
IQATestableProps &
IStyleableProps & {
    buttonClassName?: string;
    containerClassName?: string;
    style: COLLAPSE_STYLES;
    size: COLLAPSE_SIZES;
    expanded?: boolean;
};

enum COLLAPSE_SLOTS {
    TITLE,
    CONTENT,
}

export function OzCollapse (props: ICollapseProps): ReactElement {
    const {
        'data-testid': dataTestId = '',
        buttonClassName = '',
        className = '',
        containerClassName = '',
        children,
        style,
        size,
        expanded = false,
    }: ICollapseProps = props;

    const [shouldShow, setShouldShow]: [boolean, ISetState<boolean>] =
        useState<boolean>(false);

    function toggleContentVisibility (): void {
        setShouldShow(!shouldShow);
    }

    useEffect((): void => {
        setShouldShow(expanded);
    }, [expanded]);

    const btnClasses: string = classNames(
        {
            'text-gray-900 font-bold font-gellix flex': true,
            'text-lg h-5': size === COLLAPSE_SIZES.LARGE,
            'text-base h-4': size === COLLAPSE_SIZES.BASE,
            'text-sm h-4': size === COLLAPSE_SIZES.SMALL,
            'justify-between w-full': style === COLLAPSE_STYLES.CONTAINED,
        },
        buttonClassName,
    );

    const iconClasses: string = classNames({
        'pt-3/2': size === COLLAPSE_SIZES.LARGE,
        [styles.smallIcon]:
            size === COLLAPSE_SIZES.BASE || COLLAPSE_SIZES.SMALL,
    });

    const titleSlot: ReactNode = useSlot(children, COLLAPSE_SLOTS.TITLE);
    const contentSlot: ReactNode = useSlot(children, COLLAPSE_SLOTS.CONTENT);

    return (
        <div className={containerClassName} data-testid={dataTestId}>
            <button className={btnClasses} onClick={toggleContentVisibility}>
                <div className="text-left">{titleSlot}</div>
                <div className={iconClasses}>
                    <CollapseIcon isContentVisible={shouldShow} size={size} />
                </div>
            </button>
            <TransitionGroup component={null}>
                {shouldShow && (
                    <CSSTransition
                        classNames={{
                            enter: styles.collapseContentEnter,
                            enterActive: styles.collapseContentEnterActive,
                            exit: styles.collapseContentExit,
                            exitActive: styles.collapseContentExitActive,
                        }}
                        timeout={500}
                    >
                        <div
                            className={classNames({
                                [className + '']: !!className,
                            })}
                        >
                            {contentSlot}
                        </div>
                    </CSSTransition>
                )}
            </TransitionGroup>
        </div>
    );
}

OzCollapse.Title = createLayoutSlot(
    COLLAPSE_SLOTS.TITLE,
    'OzCollapse.Title',
);
OzCollapse.Content = createLayoutSlot(
    COLLAPSE_SLOTS.CONTENT,
    'OzCollapse.Content',
);
